import type { AssignmentScope } from '@Application/Ai/AiConfiguration/Assignments/AssignmentScope';
import SaveUsageSettings from '@Application/Ai/AiConfiguration/Assignments/Commands/SaveUsageSettings';
import saveUsageSettings from '@Application/Ai/AiConfiguration/Assignments/saveUsageSettings';
import ignoreHandledError from '@UI/Helpers/ignoreHandledError';
import * as React from 'react';
import { useDispatch } from '@verclam/synapse';
import { Button, ConfirmDialog } from '@verclam/ui-kit';
import { Eraser } from 'lucide-react';

type Props = {
	readonly scope: AssignmentScope;
	readonly usage: string;
	readonly usageLabel: string;
	/** `formData.includesAccountOnlyFields`, threaded down rather than re-read from `scope.type`: the
	 * FormData already computed it once, in `fromView` — the only read of `scope.type` this lot adds. */
	readonly isAccountLevel: boolean;
	readonly disabled: boolean;
};

/**
 * Leaf action, dispatches itself: always writes every key of the route's body to `null`, built
 * directly rather than from the sidebar's current `UsageSettingsFormData` — an unsaved edit sitting in
 * the form must not change what this button does. At the account level there is nothing above to
 * inherit from, so the action is framed as "Tout effacer" (the most destructive action of the lot)
 * rather than "Tout remettre en héritage", used at organization/facility level.
 */
const UsageSettingsResetAction: React.FC<Props> = ({
	scope,
	usage,
	usageLabel,
	isAccountLevel,
	disabled,
}) => {
	const dispatch = useDispatch();
	const [isConfirmVisible, setConfirmVisible] = React.useState<boolean>(false);

	const handleClick = React.useCallback(() => {
		setConfirmVisible(true);
	}, []);

	const handleHide = React.useCallback(() => {
		setConfirmVisible(false);
	}, []);

	const handleAccept = React.useCallback(() => {
		const body: Record<string, unknown> = { vocabularyTerms: null, contextPrompt: null };

		if (isAccountLevel) {
			body.systemPrompt = null;
			body.temperature = null;
		}

		dispatch(saveUsageSettings(new SaveUsageSettings(scope, usage, body))).catch(ignoreHandledError);
		setConfirmVisible(false);
	}, [dispatch, scope, usage, isAccountLevel]);

	const label = isAccountLevel ? 'Tout effacer' : 'Tout remettre en héritage';
	const message = isAccountLevel
		? `Tous les réglages d’appel définis pour ${usageLabel.toLowerCase()} à ce niveau seront effacés. Plus aucun réglage ne s’appliquera nulle part pour cet usage.`
		: `Tous les réglages d’appel définis pour ${usageLabel.toLowerCase()} à ce niveau seront retirés. L’usage redeviendra hérité du niveau au-dessus.`;

	return (
		<React.Fragment>
			<Button
				type="button"
				severity="danger"
				outlined
				label={label}
				icon={<Eraser size={16} />}
				onClick={handleClick}
				disabled={disabled}
			/>
			<ConfirmDialog
				blockScroll
				closable
				draggable={false}
				visible={isConfirmVisible}
				onHide={handleHide}
				header={label}
				message={<p>{message}</p>}
				accept={handleAccept}
				reject={handleHide}
				acceptSeverity="danger"
			/>
		</React.Fragment>
	);
};

export default UsageSettingsResetAction;
